<script setup lang="ts">
  import DynamicVariableInput from '@/components/form/DynamicVariableInput.vue'
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    FormFieldContainer,
    getValueByPath,
    InputField,
    isRecord,
    SwitchButton,
    useXPathAction,
    type SelectElement,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, reactive } from 'vue'

  const field = defineModel<SelectElement>({ required: true })
  const xpath = useXPathAction()

  const touched = reactive({
    storeLocation: false,
    listSourcePath: false,
    valueKey: false,
  })

  const items = computed((): unknown[] => {
    const valueArray = xpath.resolvePath(field.value.listSourcePath)

    return Array.isArray(valueArray)
      ? valueArray.filter(entry => isRecord(entry) || isPrimitiveOption(entry))
      : []
  })

  const item = computed(() => items.value.find(isRecord))

  const isPrimitiveList = computed(() => items.value.length > 0 && item.value == null)

  const preview = computed(() =>
    items.value.slice(0, 3).map(entry => {
      if (isRecord(entry)) {
        return {
          label: xpath.getValue(field.value.labelTemplate, entry),
          value: field.value.valueKey ? getValueByPath(field.value.valueKey, entry) : undefined,
        }
      }

      const value = String(entry)
      const template = field.value.labelTemplate
      return { label: template ? xpath.getValue(template.replaceAll('${.}', value)) : value, value }
    })
  )

  function isPrimitiveOption(entry: unknown): entry is string | number {
    return typeof entry === 'string' || typeof entry === 'number'
  }

  function validateRequired(key: 'storeLocation' | 'listSourcePath' | 'valueKey', message: string) {
    if (key === 'valueKey' && isPrimitiveList.value) return null
    return touched[key] && field.value?.type === 'select' && !field.value?.[key]?.trim()
      ? message
      : null
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${customer.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Placeholder">
      <FlowPathInput
        placeholder="Placeholder text"
        hint="Shown while no option is selected. Supports ${...} interpolation."
        v-model="field.placeholder"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer
      label="Store Location"
      :error="validateRequired('storeLocation', 'Store Location is required')"
    >
      <InputField
        placeholder="x.path.to.location"
        hint="Where the value of the selected option is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <DynamicVariableInput
      label="List source path"
      :error="validateRequired('listSourcePath', 'List source path is required')"
      placeholder="context.path.to.list"
      hint="Path to the array the options are built from."
      v-model="field.listSourcePath"
      type="text"
      @blur="touched.listSourcePath = true"
      :optional="false"
      :validate-paths="true"
    />

    <FlowPathInput
      label="Option label template"
      placeholder="e.g. ${name} (${code})"
      hint="Label of each option, resolved against the list item."
      v-model="field.labelTemplate"
      :optional="false"
      type="text"
      :validate-paths="true"
      :context="item"
    />

    <FormFieldContainer
      label="Option value key"
      :error="validateRequired('valueKey', 'Option value key is required')"
    >
      <InputField
        placeholder="e.g. code"
        hint="Path within the list item to the value that is stored."
        v-model="field.valueKey"
        type="text"
        @blur="touched.valueKey = true"
      />
    </FormFieldContainer>

    <FormFieldContainer v-if="preview.length > 0" label="Preview">
      <ul class="preview">
        <li v-for="(option, index) in preview" :key="index">
          {{ option.label }} <span class="preview-value">→ {{ option.value ?? 'undefined' }}</span>
        </li>
      </ul>
    </FormFieldContainer>

    <FormFieldContainer label="Default value">
      <FlowPathInput
        placeholder="Value of the option to preselect"
        hint="Only applied when it matches an option's value."
        v-model="field.defaultValue"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Empty message">
      <FlowPathInput
        placeholder="e.g. No options available"
        hint="Shown when the list source has no options."
        v-model="field.emptyMessage"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Required">
      <SwitchButton v-model="field.required" />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .preview {
    margin: 0;
    padding-left: 1rem;
  }

  .preview-value {
    color: var(--colours-grey-4);
  }
</style>
